import {
  API_REQUEST_TIMEOUT_MS,
  EvoluaApiClient,
  mapFetchFailureToApiError,
} from "@evolua/api-client";
import { getApiBaseUrl } from "./config.js";
import {
  isJwtUnauthorizedProblem,
  shouldAttemptSessionRefresh,
  type ProblemSnippet,
} from "./session-loss.js";
import { getIdToken } from "./token-storage.js";

type RefreshFn = () => Promise<boolean>;
type OnSessionLost = (reason: "expired") => void;
type TenantIdGetter = () => string | undefined;

let refreshFn: RefreshFn | null = null;
let onSessionLost: OnSessionLost | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let tenantIdGetter: TenantIdGetter | null = null;

/** Registrado pelo AuthProvider — evita import circular. */
export function bindAuthHttpHandlers(handlers: {
  refresh: RefreshFn;
  onSessionLost: OnSessionLost;
}): void {
  refreshFn = handlers.refresh;
  onSessionLost = handlers.onSessionLost;
}

/** Registrado pelo SessionProvider — x-tenant-id nas chamadas tipadas. */
export function bindTenantIdGetter(getter: TenantIdGetter | null): void {
  tenantIdGetter = getter;
  client = null;
}

async function tryRefreshOnce(): Promise<boolean> {
  if (!refreshFn) return false;
  if (!refreshInFlight) {
    refreshInFlight = refreshFn().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function readProblem(response: Response): Promise<ProblemSnippet | null> {
  try {
    const data: unknown = await response.clone().json();
    if (!data || typeof data !== "object") return null;
    return data as ProblemSnippet;
  } catch {
    return null;
  }
}

/**
 * fetch autenticado: Bearer (ID token) + um retry após refresh em 401 de JWT.
 * Não tenta refresh em rotas /v1/auth/* nem em 401 de tenant/permissão.
 */
export async function authFetch(
  input: string,
  init: RequestInit = {},
): Promise<Response> {
  const url = input.startsWith("http") ? input : `${getApiBaseUrl()}${input}`;
  const isAuthRoute = /\/v1\/auth\//.test(url);

  const withBearer = async (): Promise<RequestInit> => {
    const headers = new Headers(init.headers);
    const token = getIdToken();
    if (token && !headers.has("Authorization")) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    const tenantId = tenantIdGetter?.();
    if (tenantId && !headers.has("x-tenant-id")) {
      headers.set("x-tenant-id", tenantId);
    }
    const { signal: userSignal, ...rest } = init;
    const timeoutSignal = AbortSignal.timeout(API_REQUEST_TIMEOUT_MS);
    const anyFn = (
      AbortSignal as typeof AbortSignal & {
        any?: (signals: AbortSignal[]) => AbortSignal;
      }
    ).any;
    const signal =
      userSignal && typeof anyFn === "function"
        ? anyFn([userSignal, timeoutSignal])
        : timeoutSignal;
    return { ...rest, headers, signal };
  };

  let response: Response;
  try {
    response = await fetch(url, await withBearer());
  } catch (err) {
    throw mapFetchFailureToApiError(err);
  }

  if (response.status !== 401 || isAuthRoute || !refreshFn) {
    return response;
  }

  const problem = await readProblem(response);
  if (!shouldAttemptSessionRefresh(response.status, problem)) {
    return response;
  }

  const refreshed = await tryRefreshOnce();
  if (!refreshed) {
    onSessionLost?.("expired");
    return response;
  }

  try {
    response = await fetch(url, await withBearer());
  } catch (err) {
    throw mapFetchFailureToApiError(err);
  }
  if (response.status === 401) {
    const retryProblem = await readProblem(response);
    if (isJwtUnauthorizedProblem(retryProblem)) {
      onSessionLost?.("expired");
    }
  }
  return response;
}

let client: EvoluaApiClient | null = null;

export function getApiClient(): EvoluaApiClient {
  if (!client) {
    client = new EvoluaApiClient({
      baseUrl: getApiBaseUrl(),
      // ID token: api-core precisa de custom:tenant_id (ausente no access token).
      getAccessToken: () => getIdToken() ?? undefined,
      getTenantId: () => tenantIdGetter?.(),
      fetchImpl: ((input, init) =>
        authFetch(
          typeof input === "string" ? input : String(input),
          init ?? {},
        )) as typeof fetch,
    });
  }
  return client;
}
